import { useState } from "react";
import { useLocation } from "wouter";
import { Lock, Eye, EyeOff, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

export default function BankerChangePassword() {
  const [, navigate] = useLocation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mustChange = localStorage.getItem("banker_must_change_password") === "true";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords don't match."); return; }
    if (newPassword === currentPassword) { setError("New password must differ from your temporary password."); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("banker_token");
      const res = await fetch(`${BASE}api/bankers/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (res.ok) {
        localStorage.removeItem("banker_must_change_password");
        navigate("/banker");
      } else {
        const err = await res.json();
        setError(err.error || "Could not change password.");
      }
    } catch {
      setError("Unable to connect. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="fixed inset-0 pointer-events-none opacity-[0.08]" style={{
        backgroundImage: "radial-gradient(circle at 15% 20%, #FF5C00 0%, transparent 45%), radial-gradient(circle at 85% 80%, #FF5C00 0%, transparent 35%)"
      }} />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-[#FF5C00] flex items-center justify-center font-serif text-lg font-bold">F</div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Favcy Select</h1>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/50">Advisor portal</p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur p-8">
          {mustChange && (
            <div className="flex items-start gap-3 mb-6 p-4 rounded-lg bg-[#FF5C00]/10 border border-[#FF5C00]/30">
              <CheckCircle2 className="w-5 h-5 text-[#FF5C00] mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-white">First-time login</p>
                <p className="text-white/70 mt-1">Replace the temporary password from your welcome email with one only you know.</p>
              </div>
            </div>
          )}

          <h2 className="text-2xl font-serif text-white mb-2">Change your password</h2>
          <p className="text-sm text-white/60 mb-6">Minimum 8 characters. Use something memorable but not obvious.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field
              label={mustChange ? "Temporary password (from welcome email)" : "Current password"}
              value={currentPassword} onChange={setCurrentPassword}
              show={showCurrent} onToggleShow={() => setShowCurrent(!showCurrent)}
            />
            <Field
              label="New password" value={newPassword} onChange={setNewPassword}
              show={showNew} onToggleShow={() => setShowNew(!showNew)}
            />
            <Field
              label="Confirm new password" value={confirmPassword} onChange={setConfirmPassword}
              show={showNew} onToggleShow={() => setShowNew(!showNew)}
            />

            {error && <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-300">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#FF5C00] text-white font-medium hover:bg-[#FF5C00]/90 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Set new password <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, show, onToggleShow }: {
  label: string; value: string; onChange: (s: string) => void; show: boolean; onToggleShow: () => void;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#FF5C00] focus:outline-none"
        />
        <button type="button" onClick={onToggleShow} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
